import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  query,
  orderBy,
  where,
  serverTimestamp,
  Timestamp,
  getDoc,
  documentId,
  DocumentData,
  QueryDocumentSnapshot,
} from 'firebase/firestore'
import { db } from '../firebase'
// Types
export interface Task {
  id?: string
  title: string
  studentId: string
  studentName?: string
  dueDate: string | null
  startDate?: string | null
  notes: string
  status: 'pending' | 'in-progress' | 'completed'
  reference?: string
  lessonId?: string
  createdAt?: string | null
  updatedAt?: string | null
  completedDate?: string | null // Added: timestamp when task was marked completed
}
// Global cache to store tasks
// This will persist across component unmounts/remounts during navigation
export const tasksCache = {
  data: null,
  timestamp: 0,
  userId: null,
}
// Task cache validity duration - 5 minutes
export const CACHE_VALIDITY_DURATION = 5 * 60 * 1000
// Cache to store student name lookup results
const studentNameCache: Record<string, { name: string; timestamp: number }> = {}
const STUDENT_CACHE_VALIDITY = 30 * 60 * 1000 // 30 minutes
/**
 * Get all tasks for a teacher - optimized version
 * @param teacherId The ID of the teacher
 * @returns Array of task objects
 */
export const getTeacherTasks = async (teacherId: string): Promise<Task[]> => {
  if (!teacherId) {
    console.error('No teacher ID provided to getTeacherTasks')
    return []
  }
  try {
    console.log('Starting optimized task fetch for teacher:', teacherId)
    const tasksRef = collection(db, 'teachers', teacherId, 'tasks')
    const tasksQuery = query(tasksRef, orderBy('dueDate', 'asc'))
    const tasksSnapshot = await getDocs(tasksQuery)
    console.log(`Found ${tasksSnapshot.docs.length} tasks in main collection`)
    const taskPromises = tasksSnapshot.docs.map(async (taskDoc) => {
      const data = taskDoc.data()
      const studentId = data.studentId
      // Get student name (from cache if available)
      let studentName = 'Unknown Student'
      if (studentId) {
        const now = Date.now()
        const cachedStudent = studentNameCache[studentId]
        if (
          cachedStudent &&
          now - cachedStudent.timestamp < STUDENT_CACHE_VALIDITY
        ) {
          studentName = cachedStudent.name
          console.log(
            `Using cached student name for ${studentId}: ${studentName}`,
          )
        } else {
          try {
            const studentRef = doc(
              db,
              'teachers',
              teacherId,
              'students',
              studentId,
            )
            const studentDoc = await getDoc(studentRef)
            if (studentDoc.exists()) {
              studentName = studentDoc.data().name || 'Unknown Student'
              studentNameCache[studentId] = {
                name: studentName,
                timestamp: now,
              }
            }
          } catch (err) {
            console.error(`Error fetching student name for ${studentId}:`, err)
          }
        }
      }
      return {
        id: taskDoc.id,
        title: data.title || '',
        studentId: studentId || '',
        studentName: studentName,
        dueDate: data.dueDate
          ? data.dueDate instanceof Timestamp
            ? data.dueDate.toDate().toISOString()
            : data.dueDate
          : null,
        startDate: data.startDate
          ? data.startDate instanceof Timestamp
            ? data.startDate.toDate().toISOString()
            : data.startDate
          : null,
        notes: data.notes || '',
        status: data.status || 'pending',
        reference: data.reference || '',
        lessonId: data.lessonId || undefined,
        createdAt: data.createdAt
          ? data.createdAt instanceof Timestamp
            ? data.createdAt.toDate().toISOString()
            : data.createdAt
          : null,
        updatedAt: data.updatedAt
          ? data.updatedAt instanceof Timestamp
            ? data.updatedAt.toDate().toISOString()
            : data.updatedAt
          : null,
        completedDate: data.completedDate
          ? data.completedDate instanceof Timestamp
            ? data.completedDate.toDate().toISOString()
            : data.completedDate
          : null,
      } as Task
    })
    const tasks = await Promise.all(taskPromises)
    tasks.sort((a, b) => {
      const dateA =
        a.dueDate instanceof Timestamp
          ? a.dueDate.toDate()
          : new Date(a.dueDate as string)
      const dateB =
        b.dueDate instanceof Timestamp
          ? b.dueDate.toDate()
          : new Date(b.dueDate as string)
      return dateA.getTime() - dateB.getTime()
    })
    console.log(`Processed ${tasks.length} tasks with student names`)
    return tasks
  } catch (error) {
    console.error('Error fetching teacher tasks:', error)
    return []
  }
}
/**
 * Get tasks for a specific student
 * @param teacherId The ID of the teacher
 * @param studentId The ID of the student (document ID)
 * @returns Array of task objects for the student
 */
export const getStudentTasks = async (
  teacherId: string,
  studentId: string,
): Promise<Task[]> => {
  if (!teacherId || !studentId) {
    console.error('Missing teacher ID or student ID')
    return []
  }
  try {
    console.log(`Fetching tasks for student document ID: ${studentId}`)
    const tasksRef = collection(db, 'teachers', teacherId, 'tasks')
    const tasksQuery = query(
      tasksRef,
      where('studentId', '==', studentId),
      orderBy('dueDate', 'asc'),
    )
    const tasksSnapshot = await getDocs(tasksQuery)
    let studentName = 'Unknown Student'
    try {
      const studentDoc = await getDoc(
        doc(db, 'teachers', teacherId, 'students', studentId),
      )
      if (studentDoc.exists()) {
        studentName = studentDoc.data().name || 'Unknown Student'
      }
    } catch (err) {
      console.error(`Error fetching student name for ${studentId}:`, err)
    }
    return tasksSnapshot.docs.map((doc) => {
      const data = doc.data()
      return {
        id: doc.id,
        title: data.title || '',
        studentId: studentId,
        studentName: studentName,
        dueDate: data.dueDate
          ? data.dueDate instanceof Timestamp
            ? data.dueDate.toDate().toISOString()
            : data.dueDate
          : null,
        startDate: data.startDate
          ? data.startDate instanceof Timestamp
            ? data.startDate.toDate().toISOString()
            : data.startDate
          : null,
        notes: data.notes || '',
        status: data.status || 'pending',
        reference: data.reference || '',
        lessonId: data.lessonId || undefined,
        createdAt: data.createdAt
          ? data.createdAt instanceof Timestamp
            ? data.createdAt.toDate().toISOString()
            : data.createdAt
          : null,
        updatedAt: data.updatedAt
          ? data.updatedAt instanceof Timestamp
            ? data.updatedAt.toDate().toISOString()
            : data.updatedAt
          : null,
        completedDate: data.completedDate
          ? data.completedDate instanceof Timestamp
            ? data.completedDate.toDate().toISOString()
            : data.completedDate
          : null,
      } as Task
    })
  } catch (error) {
    console.error('Error fetching student tasks:', error)
    return []
  }
}
/**
 * Add a new task for a student
 * @param teacherId The ID of the teacher
 * @param studentId The ID of the student (document ID)
 * @param task The task data to add
 * @returns The ID of the newly created task
 */
export const addTask = async (
  teacherId: string,
  studentId: string,
  task: Omit<Task, 'id' | 'createdAt' | 'updatedAt'>,
): Promise<string> => {
  if (!teacherId || !studentId) {
    throw new Error('Missing teacher ID or student ID')
  }

  try {
    console.log(`Adding task for student document ID: ${studentId}`)

    const tasksRef = collection(db, 'teachers', teacherId, 'tasks')

    let dueDate = task.dueDate
    if (typeof dueDate === 'string') {
      // Fix timezone issue: treat date as local date, not UTC
      const dateObj = new Date(dueDate)
      // If the date string is date-only (YYYY-MM-DD), normalize to local midnight
      if (dueDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const [year, month, day] = dueDate.split('-').map(Number)
        const localDate = new Date(year, month - 1, day, 0, 0, 0, 0)
        dueDate = Timestamp.fromDate(localDate)
      } else {
        dueDate = Timestamp.fromDate(dateObj)
      }
    }

    let startDate = null
    if (task.startDate && typeof task.startDate === 'string') {
      const dateObj = new Date(task.startDate)
      // If the date string is date-only (YYYY-MM-DD), normalize to local midnight
      if (task.startDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const [year, month, day] = task.startDate.split('-').map(Number)
        const localDate = new Date(year, month - 1, day, 0, 0, 0, 0)
        startDate = Timestamp.fromDate(localDate)
      } else {
        startDate = Timestamp.fromDate(dateObj)
      }
    }

    const taskData: any = {
      title: task.title,
      studentId: studentId,
      studentName: task.studentName || '',
      dueDate: dueDate,
      startDate: startDate,
      notes: task.notes || '',
      status: task.status || 'pending',
      reference: task.reference || '',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    }

    // Add lessonId if provided
    if (task.lessonId) {
      taskData.lessonId = task.lessonId
    }

    const docRef = await addDoc(tasksRef, taskData)
    console.log('Task added with ID:', docRef.id)

    if (task.studentName) {
      studentNameCache[studentId] = {
        name: task.studentName,
        timestamp: Date.now(),
      }
    }

    return docRef.id
  } catch (error) {
    console.error('Error adding task:', error)
    throw error
  }
}
/**
 * Update an existing task
 * @param teacherId The ID of the teacher
 * @param studentId The ID of the student (document ID)
 * @param taskId The ID of the task to update
 * @param task The updated task data
 * @returns A boolean indicating success
 */
export const updateTask = async (
  teacherId: string,
  studentId: string,
  taskId: string,
  task: Partial<Task>,
): Promise<boolean> => {
  if (!teacherId || !studentId || !taskId) {
    throw new Error('Missing teacher ID, student ID, or task ID')
  }

  try {
    console.log(`Updating task ${taskId} for student document ID: ${studentId}`)

    const taskRef = doc(db, 'teachers', teacherId, 'tasks', taskId)

    let dueDate = task.dueDate
    if (dueDate && typeof dueDate === 'string') {
      // Fix timezone issue: treat date as local date, not UTC
      const dateObj = new Date(dueDate)
      // If the date string is date-only (YYYY-MM-DD), normalize to local midnight
      if (dueDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const [year, month, day] = dueDate.split('-').map(Number)
        const localDate = new Date(year, month - 1, day, 0, 0, 0, 0)
        dueDate = Timestamp.fromDate(localDate)
      } else {
        dueDate = Timestamp.fromDate(dateObj)
      }
    }

    let startDate = task.startDate
    if (startDate && typeof startDate === 'string') {
      const dateObj = new Date(startDate)
      // If the date string is date-only (YYYY-MM-DD), normalize to local midnight
      if (startDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const [year, month, day] = startDate.split('-').map(Number)
        const localDate = new Date(year, month - 1, day, 0, 0, 0, 0)
        startDate = Timestamp.fromDate(localDate)
      } else {
        startDate = Timestamp.fromDate(dateObj)
      }
    }

    const updateData: any = {
      updatedAt: serverTimestamp(),
    }

    if (task.title !== undefined) updateData.title = task.title
    if (dueDate !== undefined && dueDate !== null) updateData.dueDate = dueDate
    if (startDate !== undefined && startDate !== null)
      updateData.startDate = startDate
    if (task.notes !== undefined) updateData.notes = task.notes
    if (task.status !== undefined) {
      updateData.status = task.status
      // Add completedDate when task is marked as completed
      if (task.status === 'completed') {
        updateData.completedDate = serverTimestamp()
      }
    }
    if (task.reference !== undefined) updateData.reference = task.reference

    // Handle lessonId updates
    if (task.lessonId !== undefined) {
      updateData.lessonId = task.lessonId
    }

    await updateDoc(taskRef, updateData)
    console.log('Task updated successfully:', taskId)

    return true
  } catch (error) {
    console.error('Error updating task:', error)
    throw error
  }
}
/**
 * Delete a task
 * @param teacherId The ID of the teacher
 * @param studentId The ID of the student (document ID)
 * @param taskId The ID of the task to delete
 * @returns A boolean indicating success
 */
export const deleteTask = async (
  teacherId: string,
  studentId: string,
  taskId: string,
): Promise<boolean> => {
  if (!teacherId || !studentId || !taskId) {
    throw new Error('Missing teacher ID, student ID, or task ID')
  }
  try {
    console.log(`Deleting task ${taskId} for student document ID: ${studentId}`)
    // Delete from main tasks collection
    const taskRef = doc(db, 'teachers', teacherId, 'tasks', taskId)
    await deleteDoc(taskRef)
    console.log('Task deleted successfully:', taskId)
    return true
  } catch (error) {
    console.error('Error deleting task:', error)
    throw error
  }
}
